/**
 * Validation utilities for rights-data feature
 */

interface ApiErrorBody {
  title?: string[]
  external_identifiers?: string[]
  [key: string]: string[] | undefined
}

function isHttpError(
  error: unknown,
): error is Error & { status?: number; body?: ApiErrorBody } {
  return error instanceof Error && 'body' in error
}

export function parseRightsHolderApiError(error: unknown): string {
  if (!isHttpError(error)) {
    return error instanceof Error
      ? error.message
      : 'An error occurred while saving'
  }

  const body = error.body

  if (!body || error.status !== 400) {
    return error.message || 'An error occurred while saving'
  }

  const errorMessages: string[] = []

  if (body.title && Array.isArray(body.title)) {
    errorMessages.push(...body.title)
  }

  if (body.external_identifiers && Array.isArray(body.external_identifiers)) {
    errorMessages.push(
      ...body.external_identifiers.map(err =>
        // replace lowercase isni / ipi to uppercase for UI message
        err.replace(/\b(isni|ipi)\b/gi, match => match.toUpperCase()),
      ),
    )
  }

  if (errorMessages.length > 0) {
    return errorMessages.join(' ')
  }

  return error.message || 'An error occurred while saving'
}

/**
 * Checks if a name already exists in a list of strings (case-insensitive).
 * Used for create mode where we check against a list of existing names.
 *
 * @param name - The name to check for duplicates
 * @param existingNames - Array of existing name strings
 * @returns The original name of the duplicate if found, or null if no duplicate exists
 */
export function findDuplicateName(
  name: string,
  existingNames: string[],
): string | null

/**
 * Checks if a name already exists in a list of objects (case-insensitive).
 * Used for edit mode where we need to exclude the current item by ID.
 *
 * @param name - The name to check for duplicates
 * @param existingItems - Array of objects with id and name properties
 * @param excludeId - ID to exclude from comparison (for edit mode)
 * @returns The original name of the duplicate if found, or null if no duplicate exists
 */
export function findDuplicateName<T extends { id: number; name: string }>(
  name: string,
  existingItems: T[],
  excludeId: number,
): string | null

/**
 * Implementation of findDuplicateName with function overloads.
 *
 * This function uses overloads to provide type-safe duplicate detection:
 * - When called with a string array, it checks names directly
 * - When called with an object array and excludeId, it excludes the item being edited
 */
export function findDuplicateName<T extends { id: number; name: string }>(
  name: string,
  existingItems: T[] | string[],
  excludeId?: number,
): string | null {
  const trimmedName = name.trim().toLowerCase()
  if (!trimmedName) return null

  // Empty array - no duplicates possible
  if (existingItems.length === 0) return null

  // When excludeId is provided, we're in edit mode with object array
  if (excludeId !== undefined) {
    const items = existingItems as T[]
    const duplicate = items.find(
      item => item.name.toLowerCase() === trimmedName && item.id !== excludeId,
    )
    return duplicate?.name || null
  }

  // Otherwise, we're in create mode with string array
  const names = existingItems as string[]
  const duplicate = names.find(
    existing => existing.toLowerCase() === trimmedName,
  )
  return duplicate || null
}
